/**
 * Display-field configuration for the FDA Source Product Search feature
 * (docs/features/fda-source-product-search.md, Task FSS-007, Sub-task 7.2).
 *
 * New sibling to externalLinkRecordAdapter.ts per Architecture Decision 11
 * — zero edits to that file. Field lists here match exactly what
 * api/sql/fdaSourceSearchConfig.js's `displayColumns` projects for each
 * source (Requirement 2's field mapping table), not the superset of
 * fields the "Link to Source" modal's own adapter shows — the two
 * features' API responses are different shapes.
 */

import { formatDateTime } from '../../../utils/dateConversion';
import { formatDmfType, formatDmfStatus } from '../../../utils/dmfTypeLabels';
import { parseNdcActiveIngredientNames } from '../../../utils/ndcActiveIngredients';
import {
  FdaSourceKey,
  FdaSourceRow,
} from '../../../services/api/domains/products/types';

export interface FdaSourceDisplayField {
  /** A key on the source's own row shape (FdaSourceObRow/FdaSourceNdcRow/FdaSourceDmfRow, types.ts). */
  key: string;
  label: string;
  /** True for the 2-3 fields shown in the compact row; every field (summary or not) appears in the expanded "view details". */
  summary?: boolean;
  format?: (value: unknown) => string;
}

function formatText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function formatDateField(value: unknown): string {
  return formatDateTime(value) || '—';
}

function formatFinished(value: unknown): string {
  if (value === null || value === undefined) return '—';
  return value ? 'Finished' : 'Unfinished';
}

function formatDmfTypeField(value: unknown): string {
  return formatDmfType(typeof value === 'string' ? value : null);
}

function formatDmfStatusField(value: unknown): string {
  return formatDmfStatus(typeof value === 'string' ? value : null);
}

/**
 * Formats the raw `active_ingredients` JSON array string as a comma-joined
 * display string, via the shared `ndcActiveIngredients.ts` parser
 * (2026-09-23 follow-up feedback -- this used to re-implement the same JSON
 * parse locally, duplicating a util already shared by NDCPanel.tsx and
 * externalLinkRecordAdapter.ts).
 */
function formatActiveIngredients(value: unknown): string {
  if (typeof value !== 'string' || !value) return '—';
  const names = parseNdcActiveIngredientNames(value);
  return names.length > 0 ? names.join(', ') : '—';
}

const OB_FIELDS: FdaSourceDisplayField[] = [
  { key: 'trade_name', label: 'Trade Name', summary: true },
  { key: 'ingredient', label: 'Ingredient', summary: true },
  { key: 'applicant_full_name', label: 'Applicant' },
  { key: 'appl_type', label: 'Application Type' },
  { key: 'appl_no', label: 'Application No.' },
  { key: 'product_no', label: 'Product No.' },
  { key: 'approval_date', label: 'Approval Date', format: formatDateField },
  { key: 'te_code', label: 'TE Code' },
  { key: 'strength', label: 'Strength' },
  { key: 'df', label: 'Dosage Form' },
  { key: 'route', label: 'Route' },
  { key: 'data_file_date', label: 'Data File Date', format: formatDateField },
];

const NDC_FIELDS: FdaSourceDisplayField[] = [
  { key: 'generic_name', label: 'Generic Name', summary: true },
  { key: 'product_ndc', label: 'NDC', summary: true },
  { key: 'labeler_name', label: 'Labeler' },
  {
    key: 'active_ingredients',
    label: 'Active Ingredients',
    format: formatActiveIngredients,
  },
  { key: 'product_type', label: 'Product Type' },
  { key: 'finished', label: 'Status', format: formatFinished },
  {
    key: 'marketing_start_date',
    label: 'Marketing Start',
    format: formatDateField,
  },
  {
    key: 'listing_expiration_date',
    label: 'Listing Expiration',
    format: formatDateField,
  },
  { key: 'data_file_date', label: 'Data File Date', format: formatDateField },
];

const DMF_FIELDS: FdaSourceDisplayField[] = [
  { key: 'subject', label: 'Subject', summary: true },
  { key: 'holder', label: 'Holder', summary: true },
  { key: 'dmf_number', label: 'DMF Number' },
  { key: 'dmf_type', label: 'Type', format: formatDmfTypeField },
  { key: 'status', label: 'Status', format: formatDmfStatusField },
  { key: 'submit_date', label: 'Submit Date', format: formatDateField },
  { key: 'data_file_date', label: 'Data File Date', format: formatDateField },
];

/** Full per-source field list -- keyed so callers never hard-code which array belongs to which source. */
export const FDA_SOURCE_DISPLAY_FIELDS: Record<
  FdaSourceKey,
  FdaSourceDisplayField[]
> = {
  ob: OB_FIELDS,
  ndc: NDC_FIELDS,
  dmf: DMF_FIELDS,
};

/** Just the fields flagged `summary: true` -- what the compact row shows by default. */
export function getFdaSourceSummaryFields(
  source: FdaSourceKey
): FdaSourceDisplayField[] {
  return FDA_SOURCE_DISPLAY_FIELDS[source].filter(f => f.summary);
}

/** Just the fields NOT flagged `summary: true` -- shown only when a row is expanded. */
export function getFdaSourceDetailFields(
  source: FdaSourceKey
): FdaSourceDisplayField[] {
  return FDA_SOURCE_DISPLAY_FIELDS[source].filter(f => !f.summary);
}

/**
 * Resolves one display field's value from a row as a formatted string.
 * Dynamic key-on-object access, same pattern as
 * externalLinkRecordAdapter.ts's resolveExternalLinkFieldValue -- `row` is
 * a concrete discriminated union without an index signature, so the
 * `unknown` hop is TS-mandated, not an unrelated cast.
 */
export function resolveFdaSourceFieldValue(
  row: FdaSourceRow,
  field: FdaSourceDisplayField
): string {
  const bag = row as unknown as Record<string, unknown>;
  const raw = bag[field.key];
  return field.format ? field.format(raw) : formatText(raw);
}

/**
 * Resolves one display field's RAW, unformatted value -- needed (rather
 * than just `resolveFdaSourceFieldValue` above) for fields that render as
 * JSX with their own tooltip (`te_code` via `TeCodeValue`, `appl_type` via
 * `ApplTypeValue`) instead of a plain formatted string. Mirrors
 * externalLinkRecordAdapter.ts's `resolveExternalLinkFieldRawValue`.
 */
export function resolveFdaSourceFieldRawValue(
  row: FdaSourceRow,
  field: FdaSourceDisplayField
): unknown {
  const bag = row as unknown as Record<string, unknown>;
  return bag[field.key];
}

/** A row's surrogate id column name, per source -- used as the React list key. */
export function getFdaSourceRowId(
  source: FdaSourceKey,
  row: FdaSourceRow
): number {
  const bag = row as unknown as Record<string, number>;
  if (source === 'ob') return bag.fda_product_id;
  if (source === 'ndc') return bag.ndc_product_id;
  return bag.dmf_product_id;
}

/** The primary/fallback field-key pair each source's title is drawn from, in fallback order. */
const TITLE_FIELD_KEYS: Record<FdaSourceKey, [string, string]> = {
  ob: ['trade_name', 'ingredient'],
  ndc: ['generic_name', 'product_ndc'],
  dmf: ['subject', 'holder'],
};

/** The key of whichever field a row's title actually rendered (primary, or the fallback if primary was null/empty). */
export function getFdaSourceTitleFieldKey(
  source: FdaSourceKey,
  row: FdaSourceRow
): string {
  const bag = row as unknown as Record<string, unknown>;
  const [primaryKey, fallbackKey] = TITLE_FIELD_KEYS[source];
  return formatText(bag[primaryKey]) !== '—' ? primaryKey : fallbackKey;
}

/** A row's title -- the field a human would recognize the record by at a glance. */
export function getFdaSourceRowTitle(
  source: FdaSourceKey,
  row: FdaSourceRow
): string {
  const bag = row as unknown as Record<string, unknown>;
  const titleKey = getFdaSourceTitleFieldKey(source, row);
  return formatText(bag[titleKey]);
}
